import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Spinner, Button, Form, Modal } from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css";
import "./CalculatorEdit.css";
import AdminSidebar from "./AdminSidebar";
import AdministratorSidebar from "./AdministratorSidebar";
import UserSidebar from "./UserSidebar";

const CalculatorEdit = () => {
  const navigate = useNavigate();
  const [userType, setUserType] = useState("");
  const [userName, setUserName] = useState("");
  const [userFullName, setUserFullName] = useState("");
  const [userId, setUserId] = useState("");
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newCountry, setNewCountry] = useState("");
  const [newPortsAndRates, setNewPortsAndRates] = useState([{ port: "", rate: "" }]);
  const [newInspectionCharge, setNewInspectionCharge] = useState("");
  const [newHasInspection, setNewHasInspection] = useState(false);
  const [editCountry, setEditCountry] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Valid ports per country (expandable list)
  const validPorts = {
    "JAMAICA": ["Kingston", "Montego Bay"],
    "BARBADOS": ["Bridgetown", "Port St. Charles"],
    "KENYA": ["Mombasa", "Lamu"],
    "BAHAMAS": ["Nassau", "Free Port"],
  };

  // Load user data from localStorage
  useEffect(() => {
    const storedUserType = localStorage.getItem("usertype");
    const storedUserName = localStorage.getItem("userName");
    const storedFName = localStorage.getItem("fname");
    const storedLName = localStorage.getItem("lname");
    const storedUserId = localStorage.getItem("userId");

    if (!storedUserType || !storedUserId || !storedUserName) {
      localStorage.clear();
      navigate("/login");
      return;
    }

    setUserType(storedUserType);
    setUserName(storedUserName);
    setUserId(storedUserId);
    setUserFullName(storedFName && storedLName ? `${storedFName} ${storedLName}` : storedFName || storedUserName);
  }, [navigate]);

  // Fetch countries and ports
  useEffect(() => {
    setLoading(true);
    fetch("https://api.mitradingjp.com/calculator_api.php")
      .then(response => response.json())
      .then(data => {
        setCountries(data.countries || []);
        setLoading(false);
      })
      .catch(error => {
        console.error("Error fetching countries:", error);
        setLoading(false);
      });
  }, []);

  // Handle adding a new country
  const handleAddCountry = (e) => {
    e.preventDefault();
    if (!newCountry || newPortsAndRates.every(item => !item.port)) return;

    const validPortsForCountry = validPorts[newCountry.toUpperCase()];
    const filteredPortsAndRates = newPortsAndRates
      .filter(item => item.port)
      .map(item => ({
        port: item.port.trim().toLowerCase(),
        rate: item.rate ? parseFloat(item.rate) || 0 : 0
      }))
      .filter(item => validPortsForCountry?.map(p => p.toLowerCase()).includes(item.port));

    if (filteredPortsAndRates.length === 0) {
      alert("Please enter valid ports for the selected country.");
      return;
    }

    setLoading(true);
    fetch("https://api.mitradingjp.com/calculator_api.php", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        country: newCountry,
        destination_ports: filteredPortsAndRates,
        inspection_charge: parseFloat(newInspectionCharge) || 0,
        has_inspection: newHasInspection ? 1 : 0
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setCountries([...countries, {
            country: newCountry,
            destination_ports: filteredPortsAndRates,
            inspection_charge: parseFloat(newInspectionCharge) || 0,
            has_inspection: newHasInspection ? 1 : 0
          }]);
          setNewCountry("");
          setNewPortsAndRates([{ port: "", rate: "" }]);
          setNewInspectionCharge("");
          setNewHasInspection(false);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("Error adding country:", error);
        setLoading(false);
      });
  };

  // Handle editing a country
  const handleEditCountry = (country) => {
    setEditCountry({
      ...country,
      inspection_charge: country.inspection_charge || 0,
      has_inspection: country.has_inspection || 0
    });
    setShowModal(true);
  };

  const handleUpdateCountry = () => {
    if (!editCountry.country || editCountry.destination_ports.every(item => !item.port)) return;

    const validPortsForCountry = validPorts[editCountry.country.toUpperCase()];
    const currentPortsAndRates = editCountry.destination_ports.map(item => ({
      port: item.port.trim().toLowerCase(),
      rate: item.rate ? parseFloat(item.rate) || 0 : 0
    }));
    const filteredPortsAndRates = currentPortsAndRates.filter(item =>
      validPortsForCountry?.map(p => p.toLowerCase()).includes(item.port)
    );

    if (filteredPortsAndRates.length === 0) {
      alert("Please enter valid ports for the selected country.");
      return;
    } else if (filteredPortsAndRates.length !== currentPortsAndRates.length) {
      alert("Some ports were invalid and have been ignored. Only valid ports will be saved.");
    }

    setLoading(true);
    fetch("https://api.mitradingjp.com/calculator_api.php", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        id: editCountry.id,
        country: editCountry.country,
        destination_ports: filteredPortsAndRates,
        inspection_charge: parseFloat(editCountry.inspection_charge) || 0,
        has_inspection: editCountry.has_inspection ? 1 : 0
      })
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          setCountries(countries.map(c => c.id === editCountry.id ? {
            ...editCountry,
            destination_ports: filteredPortsAndRates
          } : c));
          setShowModal(false);
          setEditCountry(null);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("Error updating country:", error);
        setLoading(false);
      });
  };

  // Handle deleting a country
  const handleDeleteCountry = (id) => {
    if (window.confirm("Are you sure you want to delete this country and its ports?")) {
      setLoading(true);
      fetch("https://api.mitradingjp.com/calculator_api.php", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id })
      })
        .then(response => response.json())
        .then(data => {
          if (data.success) {
            setCountries(countries.filter(c => c.id !== id));
          }
          setLoading(false);
        })
        .catch(error => {
          console.error("Error deleting country:", error);
          setLoading(false);
        });
    }
  };

  // Add or remove port and rate input fields
  const addPortField = () => setNewPortsAndRates([...newPortsAndRates, { port: "", rate: "" }]);
  const updatePortField = (index, value) => {
    const updated = [...newPortsAndRates];
    updated[index] = { ...updated[index], port: value };
    setNewPortsAndRates(updated);
  };
  const updateRateField = (index, value) => {
    const updated = [...newPortsAndRates];
    updated[index] = { ...updated[index], rate: value };
    setNewPortsAndRates(updated);
  };
  const removePortField = (index) => {
    setNewPortsAndRates(newPortsAndRates.filter((_, i) => i !== index));
  };

  const updateEditPortField = (index, value) => {
    const updated = [...editCountry.destination_ports];
    updated[index] = { ...updated[index], port: value };
    setEditCountry({ ...editCountry, destination_ports: updated });
  };
  const updateEditRateField = (index, value) => {
    const updated = [...editCountry.destination_ports];
    updated[index] = { ...updated[index], rate: value };
    setEditCountry({ ...editCountry, destination_ports: updated });
  };
  const addEditPortField = () => {
    setEditCountry({ ...editCountry, destination_ports: [...editCountry.destination_ports, { port: "", rate: "" }] });
  };
  const removeEditPortField = (index) => {
    setEditCountry({
      ...editCountry,
      destination_ports: editCountry.destination_ports.filter((_, i) => i !== index)
    });
  };

  const logout = () => {
    localStorage.clear();
    navigate("/login");
  };

  return (
    <div className="admin-container">
      <div className="sidebar-container">
        {userType === "administrator" && <AdministratorSidebar />}
        {userType === "admin" && <AdminSidebar />}
        {userType === "user" && <UserSidebar />}
      </div>

      <div className="admin-content">
        <div className="adminnavbar">
          <h1>Welcome to the Admin Panel, {userFullName}</h1>
          <Button variant="danger" onClick={logout}>Logout</Button>
        </div>

        <div className="mainsection">
          <h3>Edit Calculator</h3>

          <Form onSubmit={handleAddCountry} className="mb-4">
            <Form.Group className="mb-3">
              <Form.Label>Country</Form.Label>
              <Form.Control
                type="text"
                value={newCountry}
                onChange={(e) => setNewCountry(e.target.value)}
                placeholder="Enter country name"
                required
                list="countrySuggestions"
              />
              <datalist id="countrySuggestions">
                {Object.keys(validPorts).map(country => (
                  <option key={country} value={country} />
                ))}
              </datalist>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Inspection Charge</Form.Label>
              <Form.Control
                type="number"
                value={newInspectionCharge}
                onChange={(e) => setNewInspectionCharge(e.target.value)}
                placeholder="Enter inspection charge"
                min="0"
                step="0.01"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Has Inspection"
                checked={newHasInspection}
                onChange={(e) => setNewHasInspection(e.target.checked)}
              />
            </Form.Group>
            <Form.Label>Destination Ports and Rates</Form.Label>
            {newPortsAndRates.map((item, index) => (
              <div key={index} className="d-flex mb-2 align-items-center">
                <Form.Control
                  type="text"
                  value={item.port}
                  onChange={(e) => updatePortField(index, e.target.value)}
                  placeholder={`Port ${index + 1}`}
                  className="me-2"
                  list={newCountry ? `portSuggestions_${index}` : null}
                  style={{ flex: 1 }}
                />
                <datalist id={`portSuggestions_${index}`}>
                  {newCountry && validPorts[newCountry.toUpperCase()]?.map(port => (
                    <option key={port} value={port} />
                  ))}
                </datalist>
                <Form.Control
                  type="number"
                  value={item.rate}
                  onChange={(e) => updateRateField(index, e.target.value)}
                  placeholder="Rate"
                  className="me-2"
                  style={{ width: "100px" }}
                  min="0"
                  step="0.01"
                />
                {newPortsAndRates.length > 1 && (
                  <Button variant="danger" onClick={() => removePortField(index)}>Remove</Button>
                )}
              </div>
            ))}
            <Button variant="secondary" onClick={addPortField} className="mb-3">Add Port</Button>
            <Button type="submit" variant="primary" disabled={loading}>
              {loading ? <Spinner size="sm" /> : "Add Country"}
            </Button>
          </Form>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Country</th>
                <th>Destination Ports & Rates</th>
                <th>Inspection Charge</th>
                <th>Has Inspection</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="text-center"><Spinner animation="border" /></td></tr>
              ) : countries.length === 0 ? (
                <tr><td colSpan="5" className="text-center">No countries found</td></tr>
              ) : (
                countries.map(country => (
                  <tr key={country.id}>
                    <td>{country.country}</td>
                    <td>
                      {country.destination_ports.map((item, index) => (
                        <div key={index} className="mb-1">
                          {item.port} - ${item.rate || 0}
                        </div>
                      ))}
                    </td>
                    <td>${country.inspection_charge || 0}</td>
                    <td>{country.has_inspection ? 'Yes' : 'No'}</td>
                    <td>
                      <Button variant="warning" onClick={() => handleEditCountry(country)} className="me-2">Edit</Button>
                      <Button variant="danger" onClick={() => handleDeleteCountry(country.id)}>Delete</Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          <Modal show={showModal} onHide={() => setShowModal(false)}>
            <Modal.Header closeButton>
              <Modal.Title>Edit Country</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {editCountry && (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Country</Form.Label>
                    <Form.Control
                      type="text"
                      value={editCountry.country}
                      onChange={(e) => setEditCountry({ ...editCountry, country: e.target.value })}
                      required
                      list="countrySuggestions"
                    />
                    <datalist id="countrySuggestions">
                      {Object.keys(validPorts).map(country => (
                        <option key={country} value={country} />
                      ))}
                    </datalist>
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Inspection Charge</Form.Label>
                    <Form.Control
                      type="number"
                      value={editCountry.inspection_charge}
                      onChange={(e) => setEditCountry({ ...editCountry, inspection_charge: e.target.value })}
                      placeholder="Enter inspection charge"
                      min="0"
                      step="0.01"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Has Inspection"
                      checked={editCountry.has_inspection}
                      onChange={(e) => setEditCountry({ ...editCountry, has_inspection: e.target.checked })}
                    />
                  </Form.Group>
                  <Form.Label>Destination Ports and Rates</Form.Label>
                  {editCountry.destination_ports.map((item, index) => (
                    <div key={index} className="d-flex mb-2 align-items-center">
                      <Form.Control
                        type="text"
                        value={item.port}
                        onChange={(e) => updateEditPortField(index, e.target.value)}
                        placeholder={`Port ${index + 1}`}
                        className="me-2"
                        list={editCountry.country ? `editPortSuggestions_${index}` : null}
                        style={{ flex: 1 }}
                      />
                      <datalist id={`editPortSuggestions_${index}`}>
                        {editCountry.country && validPorts[editCountry.country.toUpperCase()]?.map(port => (
                          <option key={port} value={port} />
                        ))}
                      </datalist>
                      <Form.Control
                        type="number"
                        value={item.rate}
                        onChange={(e) => updateEditRateField(index, e.target.value)}
                        placeholder="Rate"
                        className="me-2"
                        style={{ width: "100px" }}
                        min="0"
                        step="0.01"
                      />
                      {editCountry.destination_ports.length > 1 && (
                        <Button variant="danger" onClick={() => removeEditPortField(index)}>Remove</Button>
                      )}
                    </div>
                  ))}
                  <Button variant="secondary" onClick={addEditPortField}>Add Port</Button>
                </Form>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
              <Button variant="primary" onClick={handleUpdateCountry} disabled={loading}>
                {loading ? <Spinner size="sm" /> : "Save Changes"}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default CalculatorEdit;